import { useState, useRef } from 'react';
import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import './LocationPicker.css';

// Fix for default marker icon in webpack
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

function LocationMarker({ position, setPosition }) {
  useMapEvents({
    click(e) {
      setPosition(e.latlng);
    },
  });

  return position === null ? null : (
    <Marker position={position} />
  );
}

function LocationPicker({ initialLat, initialLon, onLocationChange, onClose }) {
  const [position, setPosition] = useState(
    initialLat && initialLon ? { lat: initialLat, lng: initialLon } : null
  );
  const [address, setAddress] = useState('');
  const [searching, setSearching] = useState(false);
  const [center, setCenter] = useState([
    initialLat || 40.7128,
    initialLon || -74.0060
  ]);
  const mapRef = useRef(null);

  const handleSearch = async () => {
    if (!address.trim()) return;

    setSearching(true);
    try {
      // Use Nominatim for geocoding
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(address)}&limit=1`
      );
      const data = await response.json();

      if (data && data.length > 0) {
        const lat = parseFloat(data[0].lat);
        const lon = parseFloat(data[0].lon);
        setPosition({ lat, lng: lon });
        setCenter([lat, lon]);

        // Fly to the location if map ref is available
        if (mapRef.current) {
          mapRef.current.flyTo([lat, lon], 13);
        }
      } else {
        alert('Location not found. Please try a different address.');
      }
    } catch (error) {
      console.error('Geocoding error:', error);
      alert('Failed to search location. Please try again.');
    } finally {
      setSearching(false);
    }
  };

  const handleConfirm = () => {
    if (position) {
      onLocationChange(position.lat, position.lng);
      onClose();
    } else {
      alert('Please select a location on the map');
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content location-picker-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Select Receiver Location</h2>
          <button className="close-button" onClick={onClose}>&times;</button>
        </div>

        <div className="location-picker-content">
          <div className="search-section">
            <input
              type="text"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Enter an address to search..."
              className="address-input"
            />
            <button
              onClick={handleSearch}
              disabled={searching}
              className="btn-search"
            >
              {searching ? 'Searching...' : 'Search'}
            </button>
          </div>

          <p className="instruction-text">
            Search for an address above, then click on the map to fine-tune the exact location.
          </p>

          {position && (
            <div className="coordinates-display">
              <strong>Selected Location:</strong> {position.lat.toFixed(6)}, {position.lng.toFixed(6)}
            </div>
          )}

          <div className="map-container">
            <MapContainer
              center={center}
              zoom={13}
              style={{ height: '400px', width: '100%' }}
              ref={mapRef}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <LocationMarker position={position} setPosition={setPosition} />
            </MapContainer>
          </div>

          <div className="form-actions">
            <button type="button" onClick={onClose} className="btn-cancel">
              Cancel
            </button>
            <button type="button" onClick={handleConfirm} className="btn-confirm">
              Confirm Location
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default LocationPicker;
